import { Button } from '@chakra-ui/button';
import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import { Icon } from '@chakra-ui/icon';
import { Flex, Box, Text, Heading, Stack } from '@chakra-ui/layout';
import { Skeleton } from '@chakra-ui/skeleton';
import { motion, AnimatePresence, isValidMotionProp } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import useStore from '../stores/store';
import { Image, Tooltip, useBoolean } from '@chakra-ui/react';
import { chakra, shouldForwardProp } from '@chakra-ui/react';
import { tooltipDownAnimation } from './customTheme';
import layoutStore from '../stores/layoutStore';
import { useItemSize } from '../Utils/useItemSize';
import usePersistStore from '../stores/persistStore';

export const MotionBox = chakra(motion.div, {
    shouldForwardProp: (prop) =>
        isValidMotionProp(prop) || shouldForwardProp(prop)
});

export const MainButton = ({
    icon,
    onClick,
    noBg = false,
    mr = '',
    ml = '',
    wsBg = false,
    letterToShow = '',
    isLoading = false
}) => {
    const hoverButton = useColorModeValue('white', '#32363A');
    const activeButton = useColorModeValue('gray.200', '#24272A');
    const wsHoverBg = useColorModeValue('gray.300', '#32363A');

    const iconToShow = {
        chevDown: ChevronDownIcon,
        collection: CollectionIcon,
        user: UserIcon,
        reminders: RemindersIcon,
        up: ArrowSmallUpIcon,
        down: ArrowSmallDownIcon,
        left: ArrowSmallLeftIcon,
        right: ArrowSmallRightIcon,
        dots: DotsIcons,
        trash: TrashDockIcon,
        share: ShareIcon
    };

    return (
        <Button
            onClick={onClick}
            size="sm"
            bg={noBg ? 'transparent' : 'var(--chakra-colors-mainButtonBg)'}
            boxShadow={noBg ? '' : 'var(--chakra-colors-mainButtonShadow)'}
            _hover={{
                bg: noBg ? (wsBg ? wsHoverBg : activeButton) : hoverButton
            }}
            _active={{
                bg: activeButton
            }}
            h="32px"
            w="32px"
            rounded="8px"
            p="0"
            alignItems={'center'}
            transition={'0.1s'}
            mr={mr}
            ml={ml}
            //isLoading={isLoading}
        >
            {isLoading ? null : icon === 'name' ? (
                <span
                    style={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: 'var(--chakra-colors-iconMd)'
                    }}
                >
                    {letterToShow}
                </span>
            ) : (
                <Icon as={iconToShow[icon]} boxSize={2} />
            )}
        </Button>
    );
};

export const WindowButton = ({ windowId }) => {
    const hoverButton = useColorModeValue('white', '#32363A');
    const activeButton = useColorModeValue('gray.50', '#24272A');

    /* const [hoverSite, hover] = useBoolean();
    const [isOpen, setIsOpen] = useState(false);
    const onClick = () => {
        console.log('todo');
    }; */

    const allActiveTabs = useStore((state) => state.allActiveTabs);
    let length = allActiveTabs[windowId]?.numberOfTabs;

    return (
        <Button
            /* onMouseEnter={hover.on}
            onMouseOver={hover.on}
            onMouseLeave={hover.off} */
            //onClick={onClick}
            size="sm"
            bg={'var(--chakra-colors-mainButtonBg)'}
            boxShadow={'var(--chakra-colors-mainButtonShadow)'}
            _hover={{
                bg: hoverButton
            }}
            _active={{
                bg: activeButton
            }}
            h="32px"
            w="32px"
            rounded="8px"
            p="0"
            alignItems={'center'}
            transition={'0.1s'}
            ml="12px"
        >
            <Text fontWeight={600} fontSize="14px" color="secondaryText">
                {length}
            </Text>
            {/* <AnimatePresence>
                {hoverSite ? (
                    <motion.div
                        layoutId="windowButton"
                        initial={{ scale: 0, y: -2 }}
                        animate={{
                            scale: 1,
                            rotate: isOpen ? 180 : 0,
                            y: isOpen ? -1 : 0
                        }}
                        exit={{ scale: 0 }}
                    >
                        <Icon as={LockIconOpen} boxSize={2} />
                    </motion.div>
                ) : (
                    <Text
                        fontWeight={600}
                        fontSize="14px"
                        color="secondaryText"
                    >
                        {length}
                    </Text>
                )}
            </AnimatePresence> */}
        </Button>
    );
};

export const LockSideButton = () => {
    const hoverButton = useColorModeValue('white', '#32363A');
    const activeButton = useColorModeValue('gray.50', '#24272A');
    const setPinSidebar = layoutStore((state) => state.setPinSidebar);
    const sidebarIsPinned = layoutStore((state) => state.sidebarIsPinned);

    const [hoverSite, hover] = useBoolean();
    //const [isLocked, setIsLocked] = useState(false);
    const onClick = () => {
        setPinSidebar(!sidebarIsPinned);
    };

    return (
        <Tooltip
            label={sidebarIsPinned ? 'Unpin sidebar' : 'Pin sidebar'}
            placement="top"
            rounded="6px"
            zIndex={1000}
            motionProps={tooltipDownAnimation}
        >
            <Button
                onMouseEnter={hover.on}
                onMouseOver={hover.on}
                onMouseLeave={hover.off}
                onClick={onClick}
                size="sm"
                bg="transparent"
                //bg={'var(--chakra-colors-mainButtonBg)'}
                //boxShadow={'var(--chakra-colors-mainButtonShadow)'}
                /* _hover={{
                    bg: hoverButton
                }} */
                _active={{
                    //bg: activeButton,
                    transform: 'scale(0.925)'
                }}
                h="32px"
                w="32px"
                rounded="8px"
                p="0"
                alignItems={'center'}
                transition={'0.1s'}
                mr="10px"
            >
                {!sidebarIsPinned ? <LockIconOpen /> : <LockIconClosed />}
            </Button>
        </Tooltip>
    );
};

export const MetaImageContainer = ({ metaUrl, hover }) => {
    const { colorMode } = useColorMode();
    const metaBg = useColorModeValue('gray.100', '#18191b');
    const metaBorder = useColorModeValue(
        '1px solid #EEF0F4',
        '1px solid #18191B'
    );
    const emptyStateText = useColorModeValue('gray.500', '#FFF');
    const fallbackImageSrc = useMemo(() => 
        colorMode === 'light'
            ? '../../assets/images/EmptyState_Bin.png'
            : '../../assets/images/EmptyState_BinDark.png',
        [colorMode]
    );

    // Use a ref to store the image element
    const imageRef = useRef(null);
    const [imageStatus, setImageStatus] = useState('loading'); // 'loading' | 'valid' | 'error'

    useEffect(() => {
        // Create a new image to test loading
        const img = document.createElement('img');
        
        img.onload = () => {
            // Check if image has dimensions
            if (img.naturalHeight + img.naturalWidth > 0) {
                setImageStatus('valid');
            } else {
                setImageStatus('error');
            }
        };
        
        img.onerror = () => {
            setImageStatus('error');
        };

        // Start loading the image
        img.src = metaUrl;

        // Cleanup
        return () => {
            img.onload = null;
            img.onerror = null;
        };
    }, [metaUrl]);

    return (
        <Box
            display="flex"
            w="100%"
            height="160px"
            alignItems="center"
            justifyContent="center"
            bg={metaBg}
            rounded={'9px'}
            mb="4px"
            pos="relative"
            pb="52.08%"
            overflow={'hidden'}
            border={metaBorder}
            transition={'0.18s cubic-bezier(.374,.019,.035,1.861)'}
        >
            {imageStatus === 'valid' && (
                <Image
                    ref={imageRef}
                    src={metaUrl}
                    h="100%"
                    w="100%"
                    alt="Meta image"
                    pos="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                />
            )}
            
            {imageStatus !== 'valid' && (
                <Box 
                    pos="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    textAlign="center"
                >
                    <Image
                        src={fallbackImageSrc}
                        boxSize="88px"
                        borderStyle="none"
                        border="0px"
                        borderColor="transparent"
                        pointerEvents="none"
                        userSelect="none"
                        margin="0 auto"
                    />
                    <Text
                        mt="12px"
                        color={emptyStateText}
                        opacity={0.5}
                        fontSize={'12px'}
                        fontWeight={600}
                    >
                        Image not found
                    </Text>
                </Box>
            )}
        </Box>
    );
};

const SidePanelIcon = ({ size = '14px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            style={{ width: size, height: size }}
        >
            <path
                d="M8 3.6C8 3.03995 8 2.75992 7.89101 2.54601C7.79513 2.35785 7.64215 2.20487 7.45399 2.10899C7.24008 2 6.96005 2 6.4 2H6.16146C5.63433 1.99998 5.17954 1.99997 4.80497 2.03057C4.40963 2.06287 4.01641 2.13419 3.63803 2.32698C3.07354 2.6146 2.6146 3.07354 2.32698 3.63803C2.13419 4.01641 2.06287 4.40963 2.03057 4.80497C1.99997 5.17954 1.99998 5.63429 2 6.16142V17.8385C1.99998 18.3657 1.99997 18.8205 2.03057 19.195C2.06287 19.5904 2.13419 19.9836 2.32698 20.362C2.6146 20.9265 3.07354 21.3854 3.63803 21.673C4.01641 21.8658 4.40963 21.9371 4.80497 21.9694C5.17954 22 5.6343 22 6.16144 22H6.4C6.96005 22 7.24008 22 7.45399 21.891C7.64215 21.7951 7.79514 21.6422 7.89101 21.454C8 21.2401 8 20.9601 8 20.4L8 3.6Z"
                fill="var(--chakra-colors-iconMd)"
            ></path>
            <path
                d="M10 20.4C10 20.9601 10 21.2401 10.109 21.454C10.2049 21.6422 10.3578 21.7951 10.546 21.891C10.7599 22 11.0399 22 11.6 22H17.8386C18.3657 22 18.8205 22 19.195 21.9694C19.5904 21.9371 19.9836 21.8658 20.362 21.673C20.9265 21.3854 21.3854 20.9265 21.673 20.362C21.8658 19.9836 21.9371 19.5904 21.9694 19.195C22 18.8205 22 18.3657 22 17.8386V6.16144C22 5.6343 22 5.17954 21.9694 4.80497C21.9371 4.40963 21.8658 4.01641 21.673 3.63803C21.3854 3.07354 20.9265 2.6146 20.362 2.32698C19.9836 2.13419 19.5904 2.06287 19.195 2.03057C18.8205 1.99997 18.3657 1.99998 17.8386 2H11.6C11.0399 2 10.7599 2 10.546 2.10899C10.3578 2.20487 10.2049 2.35785 10.109 2.54601C10 2.75992 10 3.03995 10 3.6L10 20.4Z"
                fill="var(--chakra-colors-iconMd)"
            ></path>
        </svg>
    );
};

const LockIconOpen = ({ size = '16px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: size, height: size }}
        >
            <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" />
        </svg>
    );
};

export const PlusSmallIcon = ({ size = '16px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: size, height: size }}
        >
            <path
                fillRule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clipRule="evenodd"
            />
        </svg>
    );
};

const LockIconClosed = ({ size = '16px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: size, height: size }}
        >
            <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const SearchIcon = ({
    color = 'var(--chakra-colors-iconLight)',
    size = '24px'
}) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill={color}
            style={{ width: size, height: size }}
        >
            <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const SearchIconSmall = ({
    color = 'var(--chakra-colors-iconLight)'
}) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill={color}
            style={{ width: '18px', height: '18px' }}
        >
            <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const CollectionIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '18px', height: '18px' }}
        >
            <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
        </svg>
    );
};

export const ChevronDownIcon = ({
    m = '',
    color = 'var(--chakra-colors-iconMd)'
}) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill={color}
            style={{ width: '16px', height: '16px', margin: m }}
        >
            <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const ChevronUpIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                fillRule="evenodd"
                d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const ChevronLeftSmall = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                fillRule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const ChevronRightSmall = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const CloseCircleIcon = ({ size = '16px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: size, height: size }}
        >
            <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const PlusIcon = ({ size = '16px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: size, height: size }}
        >
            <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const ChevronRightIcon = ({ m = '', size = '16px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: size, height: size, margin: m }}
        >
            <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
            />
        </svg>
    );
};

const UserIcon = ({ size = '14px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 22"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: size }}
        >
            <g>
                <circle id="Oval" cx="10" cy="5" r="5"></circle>
                <path
                    d="M9.70820393,13 L10,13 L10,13 L10.2917961,13 C14.4030266,13 18.1614018,15.3228036 20,19 L20,19 L20,19 C18.7505107,20.8742339 16.6469976,22 14.3944487,22 L5.60555128,22 C3.35300242,22 1.24948929,20.8742339 0,19 L0,19 L0,19 C1.83859819,15.3228036 5.59697339,13 9.70820393,13 Z"
                    id="Rectangle"
                ></path>
            </g>
        </svg>
    );
};

export const FolderIcon = ({
    size = '14px',
    color = 'var(--chakra-colors-iconMd)',
    m = ''
}) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill={color}
            style={{ width: size, margin: m }}
        >
            <path d="M19.5 21a3 3 0 0 0 3-3v-4.5a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h15ZM1.5 10.146V6a3 3 0 0 1 3-3h5.379a2.25 2.25 0 0 1 1.59.659l2.122 2.121c.14.141.331.22.53.22H19.5a3 3 0 0 1 3 3v1.146A4.483 4.483 0 0 0 19.5 9h-15a4.483 4.483 0 0 0-3 1.146Z" />
        </svg>
    );
};

export const BookmarkMarkIcon = ({
    size = '14px',
    color = 'var(--chakra-colors-iconMd)'
}) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill={color}
            style={{ width: size }}
        >
            <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
        </svg>
    );
};

export const LinkIcon = ({
    size = '16px',
    color = 'var(--chakra-colors-iconMd)'
}) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill={color}
            style={{ width: size }}
        >
            <path
                fillRule="evenodd"
                d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const MouseIcon = ({
    size = '16px',
    color = 'var(--chakra-colors-iconMd)'
}) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill={color}
            style={{ width: size }}
        >
            <path
                fillRule="evenodd"
                d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const DownloadIcon = ({
    size = '16px',
    color = 'var(--chakra-colors-iconMd)'
}) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill={color}
            style={{ width: size }}
        >
            <path
                fillRule="evenodd"
                d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const GlobeIcon = ({
    size = '16px',
    color = 'var(--chakra-colors-iconMd)'
}) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill={color}
            style={{ width: size }}
        >
            <path
                fillRule="evenodd"
                d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const UploadIcon = ({
    size = '16px',
    color = 'var(--chakra-colors-iconMd)'
}) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill={color}
            style={{ width: size }}
        >
            <path
                fillRule="evenodd"
                d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const LearnIcon = ({
    size = '16px',
    color = 'var(--chakra-colors-iconMd)'
}) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill={color}
            style={{ width: size }}
        >
            <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
        </svg>
    );
};

const RemindersIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px' }}
        >
            <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
        </svg>
    );
};

export const ExternalLinkIcon = ({ ml = ""}) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px', marginLeft: ml }}
        >
            <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
            <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
        </svg>
    );
};

export const EditPenIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px' }}
        >
            <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
            <path
                fillRule="evenodd"
                d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const EditPenIconFFF = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="#FFF"
            style={{ width: '16px', height: '16px' }}
        >
            <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
            <path
                fillRule="evenodd"
                d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const DuplicateIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px' }}
        >
            <path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" />
            <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
        </svg>
    );
};

export const CopyIcon = ({ size = '16px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: size, height: size }}
        >
            <path d="M8 2a1 1 0 000 2h2a1 1 0 100-2H8z" />
            <path d="M3 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v6h-4.586l1.293-1.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L10.414 13H15v3a2 2 0 01-2 2H5a2 2 0 01-2-2V5zM15 11h2a1 1 0 110 2h-2v-2z" />
        </svg>
    );
};

export const ArrowSmallUpIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                fillRule="evenodd"
                d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
                clipRule="evenodd"
            />
        </svg>
    );
};
export const ArrowSmallDownIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                fillRule="evenodd"
                d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"
                clipRule="evenodd"
            />
        </svg>
    );
};
export const ArrowSmallLeftIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                fillRule="evenodd"
                d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z"
                clipRule="evenodd"
            />
        </svg>
    );
};
export const ArrowSmallRightIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                fillRule="evenodd"
                d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const DotsIcons = ({ size = '17px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: size, height: size }}
        >
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
    );
};

export const DotsIconsFFF = ({ size = '17px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="#FFF"
            style={{ width: size, height: size }}
        >
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
    );
};

export const ChevronClose = () => {
    return (
        <svg
            viewBox="0 0 20 20"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: '16px', height: '16px', marginTop: '2px' }}
        >
            <path
                d="M3,6 C3,6 4,7 7,10 L3,14"
                stroke="var(--chakra-colors-iconMd)"
                strokeWidth="2"
            />
            <polyline
                stroke="var(--chakra-colors-iconMd)"
                strokeWidth="2"
                points="17 6 13 10 17 14"
            />
        </svg>
    );
};

/* export const CollectionIcon = () => {
    return (
        <svg
            viewBox="0 0 20 20"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                    d="M7 3C6.44772 3 6 3.44772 6 4C6 4.55228 6.44772 5 7 5H13C13.5523 5 14 4.55228 14 4C14 3.44772 13.5523 3 13 3H7Z"
                    fill="currentColor"
                />
                <path
                    d="M4 7C4 6.44772 4.44772 6 5 6H15C15.5523 6 16 6.44772 16 7C16 7.55228 15.5523 8 15 8H5C4.44772 8 4 7.55228 4 7Z"
                    fill="currentColor"
                />
                <path
                    d="M2 11C2 9.89543 2.89543 9 4 9H16C17.1046 9 18 9.89543 18 11V15C18 16.1046 17.1046 17 16 17H4C2.89543 17 2 16.1046 2 15V11Z"
                    fill="currentColor"
                />
        </svg>
    );
}; */

export const ChevronOpen = () => {
    return (
        <svg
            width="20px"
            height="20px"
            viewBox="0 0 20 20"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            stroke="none"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <g stroke="currentColor" strokeWidth="2">
                <polyline points="14 2 18 6 14 10" />
                <polyline points="6 2 2 6 6 10" />
            </g>
        </svg>
    );
};

export const ArrowCircleRight = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
            style={{ width: '20px', opacity: '0.9725', marginRight: '8px' }}
        >
            <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const ArrowCircleLeft = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '20px', opacity: '0.9725' }}
        >
            <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const PlusCircleIcon = ({ green = false }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill={green ? '#41E18B' : 'var(--chakra-colors-iconMd)'}
            style={{ width: '16px', height: '16px' }}
        >
            <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const MinusCircleIcon = ({ red = false }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            style={{ width: '16px', height: '16px' }}
            fill={red ? '#F64545' : 'var(--chakra-colors-iconMd)'}
        >
            <g>
                <path
                    d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM7 9C6.44772 9 6 9.44772 6 10C6 10.5523 6.44772 11 7 11H13C13.5523 11 14 10.5523 14 10C14 9.44772 13.5523 9 13 9H7Z"
                    fillRule="evenodd"
                />
            </g>
        </svg>
    );
};

export const RefreshIcon = ({ color = '#FFF', size = '18px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            style={{ width: size, height: size }}
        >
            <g>
                <path
                    d="M4 2C4.55228 2 5 2.44772 5 3V5.10125C6.27009 3.80489 8.04052 3 10 3C13.0494 3 15.641 4.94932 16.6014 7.66675C16.7855 8.18747 16.5126 8.75879 15.9918 8.94284C15.4711 9.12689 14.8998 8.85396 14.7157 8.33325C14.0289 6.38991 12.1755 5 10 5C8.36507 5 6.91204 5.78502 5.99935 7H9C9.55228 7 10 7.44772 10 8C10 8.55228 9.55228 9 9 9H4C3.44772 9 3 8.55228 3 8V3C3 2.44772 3.44772 2 4 2ZM4.00817 11.0572C4.52888 10.8731 5.1002 11.146 5.28425 11.6668C5.97112 13.6101 7.82453 15 10 15C11.6349 15 13.088 14.215 14.0006 13L11 13C10.4477 13 10 12.5523 10 12C10 11.4477 10.4477 11 11 11H16C16.2652 11 16.5196 11.1054 16.7071 11.2929C16.8946 11.4804 17 11.7348 17 12V17C17 17.5523 16.5523 18 16 18C15.4477 18 15 17.5523 15 17V14.8987C13.7299 16.1951 11.9595 17 10 17C6.95059 17 4.35905 15.0507 3.39857 12.3332C3.21452 11.8125 3.48745 11.2412 4.00817 11.0572Z"
                    fill={color}
                    fillRule="evenodd"
                />
            </g>
        </svg>
    );
};

export const UpdateIcon = ({
    color = 'var(--chakra-colors-iconMd)',
    size = '16px'
}) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            style={{ width: size, height: size }}
        >
            <g>
                <path
                    d="M4 2C4.55228 2 5 2.44772 5 3V5.10125C6.27009 3.80489 8.04052 3 10 3C13.0494 3 15.641 4.94932 16.6014 7.66675C16.7855 8.18747 16.5126 8.75879 15.9918 8.94284C15.4711 9.12689 14.8998 8.85396 14.7157 8.33325C14.0289 6.38991 12.1755 5 10 5C8.36507 5 6.91204 5.78502 5.99935 7H9C9.55228 7 10 7.44772 10 8C10 8.55228 9.55228 9 9 9H4C3.44772 9 3 8.55228 3 8V3C3 2.44772 3.44772 2 4 2ZM4.00817 11.0572C4.52888 10.8731 5.1002 11.146 5.28425 11.6668C5.97112 13.6101 7.82453 15 10 15C11.6349 15 13.088 14.215 14.0006 13L11 13C10.4477 13 10 12.5523 10 12C10 11.4477 10.4477 11 11 11H16C16.2652 11 16.5196 11.1054 16.7071 11.2929C16.8946 11.4804 17 11.7348 17 12V17C17 17.5523 16.5523 18 16 18C15.4477 18 15 17.5523 15 17V14.8987C13.7299 16.1951 11.9595 17 10 17C6.95059 17 4.35905 15.0507 3.39857 12.3332C3.21452 11.8125 3.48745 11.2412 4.00817 11.0572Z"
                    fill={color}
                    fillRule="evenodd"
                />
            </g>
        </svg>
    );
};

export const TrashIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                d="M9 2C8.62123 2 8.27497 2.214 8.10557 2.55279L7.38197 4H4C3.44772 4 3 4.44772 3 5C3 5.55228 3.44772 6 4 6L4 16C4 17.1046 4.89543 18 6 18H14C15.1046 18 16 17.1046 16 16V6C16.5523 6 17 5.55228 17 5C17 4.44772 16.5523 4 16 4H12.618L11.8944 2.55279C11.725 2.214 11.3788 2 11 2H9ZM7 8C7 7.44772 7.44772 7 8 7C8.55228 7 9 7.44772 9 8V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V8ZM12 7C11.4477 7 11 7.44772 11 8V14C11 14.5523 11.4477 15 12 15C12.5523 15 13 14.5523 13 14V8C13 7.44772 12.5523 7 12 7Z"
                fillRule="evenodd"
            />
        </svg>
    );
};

export const FlashIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '14px', height: '14px' }}
        >
            <path
                fillRule="evenodd"
                d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                clipRule="evenodd"
            />
        </svg>
    );
};

const ShieldWarningIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            style={{ width: '32px', height: '32px', fill: '#ff8b2a' }}
        >
            <path
                fillRule="evenodd"
                d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0110 1.944zM11 14a1 1 0 11-2 0 1 1 0 012 0zm0-7a1 1 0 10-2 0v3a1 1 0 102 0V7z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const CreditCardIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"
                fill="currentColor"
            />
            <path
                fillRule="evenodd"
                d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                clipRule="evenodd"
                fill="currentColor"
            />
        </svg>
    );
};

export const TrashDockIcon = ({ size = '16px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: size, height: size }}
        >
            <path
                fillRule="evenodd"
                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const ShareIcon = ({ size = '16px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: size, height: size }}
        >
            <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
        </svg>
    );
};

export const CommentIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                d="M18 13V5C18 3.89543 17.1046 3 16 3H4C2.89543 3 2 3.89543 2 5V13C2 14.1046 2.89543 15 4 15H7L10 18L13 15H16C17.1046 15 18 14.1046 18 13ZM5 7C5 6.44772 5.44772 6 6 6H14C14.5523 6 15 6.44772 15 7C15 7.55228 14.5523 8 14 8H6C5.44772 8 5 7.55228 5 7ZM6 10C5.44772 10 5 10.4477 5 11C5 11.5523 5.44772 12 6 12H9C9.55229 12 10 11.5523 10 11C10 10.4477 9.55229 10 9 10H6Z"
                fillRule="evenodd"
            />
        </svg>
    );
};

export const ChevronIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                d="M5.29289 7.29289C5.68342 6.90237 6.31658 6.90237 6.70711 7.29289L10 10.5858L13.2929 7.29289C13.6834 6.90237 14.3166 6.90237 14.7071 7.29289C15.0976 7.68342 15.0976 8.31658 14.7071 8.70711L10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L5.29289 8.70711C4.90237 8.31658 4.90237 7.68342 5.29289 7.29289Z"
                fillRule="evenodd"
            />
        </svg>
    );
};

export const EyeIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                opacity="0.12"
                d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                fill="currentColor"
            ></path>
            <path
                d="M2.41973 12.7132C2.28354 12.4975 2.21545 12.3897 2.17733 12.2234C2.1487 12.0985 2.1487 11.9015 2.17733 11.7766C2.21545 11.6103 2.28354 11.5025 2.41973 11.2868C3.54513 9.50484 6.895 5 12 5C17.105 5 20.4549 9.50484 21.5803 11.2868C21.7165 11.5025 21.7846 11.6103 21.8227 11.7766C21.8513 11.9015 21.8513 12.0985 21.8227 12.2234C21.7846 12.3897 21.7165 12.4975 21.5803 12.7132C20.4549 14.4952 17.105 19 12 19C6.895 19 3.54513 14.4952 2.41973 12.7132Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            ></path>
            <path
                d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3432 9 9.00001 10.3431 9.00001 12C9.00001 13.6569 10.3432 15 12 15Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            ></path>
        </svg>
    );
};

export const EyeOffIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            style={{ width: '16px', height: '16px' }}
        >
            <path
                opacity="0.12"
                d="M9 12C9 13.6568 10.3431 15 12 15C12.8284 15 13.5784 14.6642 14.1213 14.1213L9.87868 9.87866C9.33579 10.4216 9 11.1716 9 12Z"
                fill="currentColor"
            ></path>
            <path
                d="M10.7425 5.09232C11.1489 5.03223 11.5682 5 12 5C17.105 5 20.4549 9.50484 21.5803 11.2868C21.7165 11.5025 21.7846 11.6103 21.8227 11.7767C21.8513 11.9016 21.8513 12.0987 21.8227 12.2236C21.7845 12.3899 21.7159 12.4985 21.5788 12.7156C21.2789 13.1901 20.8217 13.8571 20.2161 14.5805M6.72389 6.71504C4.56182 8.1817 3.09402 10.2194 2.42068 11.2853C2.28386 11.5019 2.21545 11.6102 2.17731 11.7765C2.14867 11.9014 2.14866 12.0984 2.17729 12.2234C2.2154 12.3897 2.2835 12.4975 2.41971 12.7132C3.54511 14.4952 6.89499 19 12 19C14.0584 19 15.8315 18.2676 17.2884 17.2766M2.99999 3L21 21M9.87867 9.87868C9.33577 10.4216 8.99999 11.1716 8.99999 12C8.99999 13.6569 10.3431 15 12 15C12.8284 15 13.5784 14.6642 14.1213 14.1213"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            ></path>
        </svg>
    );
};

export const CheckCircle = ({ size = '32px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="#3BDE86"
            style={{ width: size, height: size }}
        >
            <path
                d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM13.7071 8.70711C14.0976 8.31658 14.0976 7.68342 13.7071 7.29289C13.3166 6.90237 12.6834 6.90237 12.2929 7.29289L9 10.5858L7.70711 9.29289C7.31658 8.90237 6.68342 8.90237 6.29289 9.29289C5.90237 9.68342 5.90237 10.3166 6.29289 10.7071L8.29289 12.7071C8.68342 13.0976 9.31658 13.0976 9.70711 12.7071L13.7071 8.70711Z"
                fillRule="evenodd"
            />
        </svg>
    );
};

export const PlusCircleIcon2 = ({ size = '20px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            style={{ width: size, height: size }}
        >
            <path
                fill="#41E18B"
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const ArrowCircleDownIcon = ({ size = '20px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            style={{ width: size, height: size }}
        >
            <path
                fill="var(--chakra-colors-iconMd)"
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const NoteIcon = ({ size = '20px' }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            style={{ width: size, height: size }}
        >
            <path
                fillRule="evenodd"
                d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z"
                clipRule="evenodd"
            />
        </svg>
    );
};

export const AddUserIcon = ({ size = '20px', color }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            style={{ width: size, height: size }}
        >
            <path
                fill="currentColor"
                d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z"
            />
        </svg>
    );
};

export const AlarmIcon = ({
    shadow = false,
    color = 'var(--chakra-colors-iconExtraLight)'
}) => {
    const colorVar = useColorModeValue('gray.400', 'gray.200');
    const shadowVar = useColorModeValue(
        'drop-shadow(1px 1px 0px #FFF)',
        'drop-shadow(1px 1px 0px #1e2123)'
    );

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            style={{
                width: '16px',
                height: '16px',
                filter: shadow ? shadowVar : ''
            }}
        >
            <path
                d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"
                fill={color}
            />
        </svg>
    );
};

export const AlarmIndicator = ({ hasAlarm, type, hoverSite = false }) => {
    const userData = useStore((state) => state.userData);
    let userId = userData?.userId;
    const hasAlarmWithId = hasAlarm?.includes(userId) ? true : false;

    const [shakeKey, setShakeKey] = useState(0);
    const prevHoverRef = useRef(false); // useRef to keep track of the previous hover state

    useEffect(() => {
        if (hoverSite && !prevHoverRef.current) {
            setShakeKey((prevKey) => prevKey + 1);
        }
        prevHoverRef.current = hoverSite; // Update the previous hover value
    }, [hoverSite]);

    const shakeAnimation = {
        x: ['0px', '2px', '-2px', '1px'],
        transition: {
            duration: 0.3,
            yoyo: 1
        }
    };

    const { noteAlarmPos, todoAlarmPos } = useItemSize();

    const top = {
        note: noteAlarmPos.top,
        todo: noteAlarmPos.top,
        tabWithNote: '6px',
        stack: '-2px',
        site: '-8px'
    };

    const left = {
        note: noteAlarmPos.left,
        todo: todoAlarmPos.left,
        tabWithNote: '2px',
        stack: '4px',
        site: '-8px'
    };

    if (!hasAlarmWithId) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={{
                    type: 'spring',
                    stiffness: 600,
                    damping: 26
                }}
                style={{
                    position: 'absolute',
                    top: top[type],
                    left: left[type]
                }}
            >
                <motion.div
                    key={shakeKey} // Attach the key here
                    animate={hoverSite ? shakeAnimation : {}}
                >
                    <AlarmIcon shadow />
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export const IconWrapper = ({
    icon,
    size = '20px',
    my = 'auto',
    color = 'var(--chakra-colors-iconExtraLight)',
    m = ''
}) => {
    const iconToShow = {
        externalLink: <ExternalLinkIcon />,
        editPen: <EditPenIcon />,
        alarm: <AlarmIcon color={color} />,
        trash: <TrashIcon />,
        shield: <ShieldWarningIcon />,
        credit: <CreditCardIcon />,
        addUser: <AddUserIcon color={color} />,
        collection: <CollectionIcon />,
        note: <NoteIcon />,
        plus: <PlusCircleIcon2 size={size} />
    };

    return (
        <Box h="size" w="size" my={my} m={m}>
            {iconToShow[icon]}
        </Box>
    );
};

export const TrashIconRedHover = ({ size = '14px' }) => {
    return (
        <Box
            color="var(--chakra-colors-iconMd)"
            _groupHover={{ color: '#F64545' }}
            m="auto"
            transition="0.16s"
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                style={{ width: size, height: size }}
            >
                <path
                    fillRule="evenodd"
                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                    clipRule="evenodd"
                    fill="currentColor"
                />
            </svg>
        </Box>
    );
};

export const RefreshIconGreenHover = ({ size = '16px' }) => {
    return (
        <Box
            color="var(--chakra-colors-iconMd)"
            _groupHover={{ color: '#41E18B' }}
            m="auto"
            transition="0.16s"
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                style={{ width: '14px', height: '14px', margin: 'auto' }}
            >
                <path
                    fillRule="evenodd"
                    d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                    clipRule="evenodd"
                    fill="currentColor"
                />
            </svg>
        </Box>
    );
};

export const PlusIconGreenHover = ({ size = '16px' }) => {
    return (
        <Box
            color="var(--chakra-colors-iconMd)"
            _groupHover={{
                color: '#41E18B' /* transform: 'translateX(-54px)' */
            }}
            transition={'0.16s'}
            //transition="0.3s cubic-bezier(0.175, 0.885, 0.320, 1.275)"
            m="auto"
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                style={{ width: '16px', height: '16px' /* margin: 'auto' */ }}
            >
                <path
                    fillRule="evenodd"
                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                    clipRule="evenodd"
                    fill="currentColor"
                />
            </svg>
        </Box>
    );
};

export const PlusBallIconGreenHover = ({ size = '16px' }) => {
    return (
        <Box
            color="var(--chakra-colors-iconMd)"
            _groupHover={{
                color: '#41E18B' /* transform: 'translateX(-54px)' */
            }}
            transition={'0.16s'}
            //transition="0.3s cubic-bezier(0.175, 0.885, 0.320, 1.275)"
            m="auto"
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                style={{ width: '16px', height: '16px' /* margin: 'auto' */ }}
            >
                <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                    clipRule="evenodd"
                    fill="currentColor"
                />
            </svg>
        </Box>
    );
};
